import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { Subscription, of } from 'rxjs';
import { switchMap, catchError, finalize } from 'rxjs';
import saveAs from 'file-saver';
import { Attachment } from '@models/file/model';
import { AttachmentService } from '@app/shared/services/attachment.service';
import { RouteContextProviderService } from '@app/shared/utils/routecontextprovider.service';
import { ServiceFactory } from '@app/shared/services/service.factory';
import { UtilityService } from '@app/shared/services/utility.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';

export interface AttachmentInput {
  id: string;
  friendlyFileName?: string;
}

/**
 * Component that will take a list of Attachment IDs from the Attachment table in UBA and display them in a list
 * Can optionally accept filenames alongside IDs to avoid unnecessary API calls
 */
@Component({
  selector: 'app-attachment-list',
  templateUrl: './attachment-list.component.html',
  styleUrls: ['./attachment-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatProgressSpinner, MatCheckbox, MatRadioGroup, MatRadioButton],
})
export class AttachmentListComponent implements OnInit, OnChanges, OnDestroy {
  private attachmentService = inject(AttachmentService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private serviceFactory = inject(ServiceFactory);
  private routeContextProvider = inject(RouteContextProviderService);
  private utilityService = inject(UtilityService);

  @Input() public attachmentInputs: AttachmentInput[] = [];
  @Input() public attachmentDisplayType: 'checkbox' | 'radio' | 'list' = 'checkbox';
  @Output() public selectionChanged = new EventEmitter<Set<string>>();

  public attachments: Attachment[] = [];
  public selectedFiles = new Set<string>();
  public isLoading = false;

  private attachmentSubscriptions = new Subscription();
  private previewSubscriptions = new Subscription();

  public ngOnInit(): void {
    if (this.attachmentInputs?.length) {
      this.loadAttachments();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Detect changes to attachmentInputs and reload attachments
    if (changes['attachmentInputs']) {
      const prevInputs = changes['attachmentInputs'].previousValue;
      const currentInputs = changes['attachmentInputs'].currentValue;

      // Only reload if the inputs have actually changed
      if (JSON.stringify(prevInputs) !== JSON.stringify(currentInputs)) {
        this.loadAttachments();
      }
    }
  }

  public ngOnDestroy(): void {
    this.attachmentSubscriptions.unsubscribe();
    this.previewSubscriptions.unsubscribe();
  }

  public trackByAttachmentId(index: number, attachment: Attachment): string {
    return attachment.id;
  }

  public toggleSelection(id: string): void {
    if (this.attachmentDisplayType === 'list') {
      return;
    }

    // only select one at a time
    if (this.attachmentDisplayType === 'radio') {
      this.selectedFiles.clear();
      this.selectedFiles.add(id);
    } else if (this.selectedFiles.has(id)) {
      this.selectedFiles.delete(id);
    } else {
      this.selectedFiles.add(id);
    }
    this.emitSelectionChange();
  }

  public isSelected(id: string): boolean {
    return this.selectedFiles.has(id);
  }

  public previewFile(attachmentId: string): void {
    const downloadableFileExtensions = ['csv', 'doc', 'docx', 'xlsx', 'xls'];
    const mimeTypeMap: { [key: string]: string } = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      txt: 'text/plain',
      json: 'application/json',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };

    const attachment = this.attachments.find((att) => att.id === attachmentId);

    if (!attachment || !attachment.friendlyFileName) {
      this.utilityService.showGrowelMessage('Something went wrong while fetching the attachment', false);
      return;
    }

    const fileExtension = attachment.friendlyFileName.split('.').pop()?.toLowerCase();

    const previewSub = this.attachmentService
      .getAttachmentContentUrl(attachmentId)
      .pipe(
        switchMap((url) =>
          this.serviceFactory.getFileContent(url, { Accept: 'image/jpeg, application/octet-stream, */*' }).pipe(
            switchMap((response) => {
              // Priority: 1. attachment.attachmentType if it exists
              //           2. fileExtension if available
              //           3. Default to octet-stream if neither is available
              const fileType = attachment.attachmentType || fileExtension;
              const mimeType = (fileType && mimeTypeMap[fileType]) || 'application/octet-stream';
              const blob = new Blob([response], { type: mimeType });
              const shouldDownload = fileType && downloadableFileExtensions.includes(fileType);

              if (shouldDownload) {
                saveAs(blob, attachment.friendlyFileName);
              } else {
                this.routeContextProvider.navigateTo(URL.createObjectURL(blob));
              }

              return of(null);
            }),
            catchError((_error: unknown) => {
              this.utilityService.showGrowelMessage('Something went wrong while fetching the attachment', false);
              return of(null);
            })
          )
        ),
        catchError((_error: unknown) => {
          this.utilityService.showGrowelMessage('Something went wrong while fetching the attachment', false);
          return of(null);
        })
      )
      .subscribe();

    this.previewSubscriptions.add(previewSub);
  }

  public loadAttachments(): void {
    this.attachmentSubscriptions.unsubscribe();
    this.attachmentSubscriptions = new Subscription();

    if (!this.attachmentInputs?.length) {
      this.attachments = [];
      return;
    }

    const previousAttachmentIds = new Set(this.attachments.map((att) => att.id));

    const attachmentsWithFilenames = this.attachmentInputs.filter((input) => input.friendlyFileName);
    const idsNeedingFetch = this.attachmentInputs.filter((input) => !input.friendlyFileName).map((input) => input.id);

    this.attachments = attachmentsWithFilenames.map(
      (input) =>
        ({
          id: input.id,
          friendlyFileName: input.friendlyFileName,
        }) as Attachment
    );

    if (idsNeedingFetch.length > 0) {
      this.isLoading = true;

      const attachmentSub = this.attachmentService
        .getAttachments(idsNeedingFetch)
        .pipe(
          finalize(() => {
            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
          })
        )
        .subscribe((fetchedAttachments: Attachment[]) => {
          this.attachments = [...this.attachments, ...fetchedAttachments];

          this.attachments.forEach((att) => {
            if (!previousAttachmentIds.has(att.id)) {
              // select the newly uploaded file
              if (this.attachmentDisplayType === 'radio') {
                this.selectedFiles.clear();
              }
              this.selectedFiles.add(att.id);
            }
          });

          this.emitSelectionChange();
          this.changeDetectorRef.detectChanges();
        });

      this.attachmentSubscriptions.add(attachmentSub);
    } else {
      this.attachments.forEach((att) => {
        if (!previousAttachmentIds.has(att.id)) {
          if (this.attachmentDisplayType === 'radio') {
            this.selectedFiles.clear();
          }
          this.selectedFiles.add(att.id);
        }
      });

      this.emitSelectionChange();
      this.changeDetectorRef.detectChanges();
    }
  }

  private emitSelectionChange(): void {
    this.selectionChanged.emit(new Set(this.selectedFiles));
  }
}
